"use client";

import React from "react";
import { Calendar, Tag, User, ArrowLeft } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

function PostSkeleton() {
  return (
    <div className="animate-pulse space-y-8">
      <div className="space-y-4">
        <div className="h-12 bg-muted rounded-md w-3/4"></div>
        <div className="h-12 bg-muted rounded-md w-1/2"></div>
      </div>
      <div className="flex items-center gap-4">
        <div className="size-12 bg-muted rounded-lg shrink-0"></div>
        <div className="space-y-2">
          <div className="h-4 bg-muted rounded w-32"></div>
          <div className="h-3 bg-muted rounded w-24"></div>
        </div>
      </div>
      <div className="aspect-video w-full bg-muted rounded-lg"></div>
      <div className="space-y-4 pt-4">
        <div className="h-4 bg-muted rounded w-full"></div>
        <div className="h-4 bg-muted rounded w-full"></div>
        <div className="h-4 bg-muted rounded w-11/12"></div>
        <div className="h-4 bg-muted rounded w-full"></div>
        <div className="h-4 bg-muted rounded w-4/5"></div>
      </div>
    </div>
  );
}

function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return <PostSkeleton />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive font-medium">Failed to load the journal entry.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-20">
        <h2 className="text-2xl font-bold font-default text-foreground mb-4">Story Not Found</h2>
        <p className="text-muted-foreground mb-8">The wedding story you are looking for has been moved to our archives.</p>
        <Link to="/HomePage">
          <Button variant="default" className="rounded-md">Return to Gallery</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "Ivory Lens Team";
  const authorAvatar = post.author?.avatar?.["96"] || "";
  const postDate = post.date ? new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : "";
  const category = post.categories && post.categories.length > 0 ? post.categories[0].name : "";

  return (
    <article className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Link to="/HomePage" className="inline-flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-colors mb-6">
          <ArrowLeft className="mr-2 size-4" />
          Back to Journal
        </Link>

        {category && (
          <div className="mb-4">
            <span className="inline-flex items-center rounded-md bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
              <Tag className="mr-1.5 size-3" />
              {category}
            </span>
          </div>
        )}

        <h1 className="text-3xl md:text-5xl font-bold font-default text-foreground leading-tight mb-6">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-y border-border py-4 mb-8">
          <div className="flex items-center gap-3">
            <Avatar className="size-10 border border-border">
              {authorAvatar ? (
                <AvatarImage src={authorAvatar} alt={authorName} />
              ) : (
                <AvatarFallback className="bg-secondary text-secondary-foreground">
                  <User className="size-5" />
                </AvatarFallback>
              )}
            </Avatar>
            <div>
              <p className="font-medium text-foreground font-default">{authorName}</p>
              <p className="text-xs">Lead Photographer</p>
            </div>
          </div>
          
          {postDate && (
            <div className="flex items-center gap-2 ml-auto md:ml-0">
              <Calendar className="size-4" />
              <time dateTime={post.date}>{postDate}</time>
            </div>
          )}
        </div>
      </div>

      {post.featuredImage && (
        <div className="mb-10 rounded-lg overflow-hidden bg-muted border border-border shadow-md">
          <img 
            src={post.featuredImage} 
            alt={post.title} 
            className="w-full h-auto object-cover max-h-[600px]"
          />
        </div>
      )}

      <div 
        className="text-foreground font-sans text-base md:text-lg leading-relaxed space-y-6 [&>p]:mb-6 [&>h2]:text-2xl md:[&>h2]:text-3xl [&>h2]:font-bold [&>h2]:font-default [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:text-foreground [&>h3]:text-xl md:[&>h3]:text-2xl [&>h3]:font-bold [&>h3]:font-default [&>h3]:mt-8 [&>h3]:mb-4 [&>h3]:text-foreground [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ul>li]:mb-2 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>ol>li]:mb-2 [&>a]:text-primary [&>a]:underline [&>a:hover]:text-primary/80 [&>blockquote]:border-l-4 [&>blockquote]:border-primary [&>blockquote]:pl-4 [&>blockquote]:italic [&>blockquote]:text-muted-foreground [&>blockquote]:my-6 [&>img]:rounded-lg [&>img]:my-8 [&>img]:w-full [&>img]:h-auto [&>figure]:my-8 [&>figure>img]:rounded-lg [&>figure>img]:w-full [&>figure>figcaption]:text-center [&>figure>figcaption]:text-sm [&>figure>figcaption]:text-muted-foreground [&>figure>figcaption]:mt-2"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />
    </article>
  );
}

function RelatedPostsConsumer() {
  const { posts, loading } = usePosts();

  if (loading || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto mt-16 pt-12 border-t border-border">
      <h3 className="text-2xl font-bold font-default text-foreground mb-8">More From The Journal</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post, i) => (
          <Card key={post.id} data-index={i} className="flex flex-col h-full overflow-hidden hover:shadow-lg transition-shadow duration-300 border-border bg-card rounded-lg">
            {post.featuredImage && (
              <div className="aspect-video w-full overflow-hidden bg-muted">
                <img 
                  src={post.featuredImage} 
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>
            )}
            <CardHeader className="p-5 pb-0 flex-grow">
              {post.categories && post.categories.length > 0 && (
                <span className="text-xs font-medium text-primary mb-2 block tracking-wider uppercase">
                  {post.categories[0].name}
                </span>
              )}
              <CardTitle className="text-lg font-bold font-default line-clamp-2 leading-tight text-foreground">
                {post.title}
              </CardTitle>
            </CardHeader>
            <CardFooter className="p-5 pt-4 mt-auto">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="outline" className="w-full rounded-md border-primary text-primary hover:bg-primary hover:text-primary-foreground">View Story</Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}

export default function SinglePostTemplateSection() {
  
  const currentWPQueryParams = typeof window !== 'undefined' && (window as any).wvcClient 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: 'post', per_page: 1 };
  
  const main_wp_query = new WP_Query(currentWPQueryParams);

  
  const related_wp_query = new WP_Query({ post_type: 'post', per_page: 3 });

  return (
    <section data-section-id="221660" id="single-post-template-section" className="bg-background py-12 md:py-20 lg:py-24 px-4 sm:px-6">
      <div className="container mx-auto">
        <PostsProvider wp_query={main_wp_query}>
          <SinglePostConsumer />
        </PostsProvider>
        
        <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
          <RelatedPostsConsumer />
        </PostsProvider>
      </div>
    </section>
  );
}